import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Circle, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, Minimize2, Compass } from 'lucide-react';
import { normalizeProjectGeolocation } from '../utils/projectLocation';

/**
 * The venue's "kitchen-table map" (public partner page): every adventure
 * represented by its honest geographic footprint, so visitors choose by
 * *where*, not just by card.
 *
 * - on_site_route → labeled pin ("go here")
 * - regional      → soft dashed circle ("roughly around here") — same 50 km
 *                   radius the AI guide uses to decide "in the region"
 *                   (ON_SITE_RADIUS_M in backend geo_awareness.py)
 * - anywhere      → no geometry; "playable anywhere" chip under the map
 */

const FIT_OPTIONS = { maxZoom: 15, padding: [48, 48] };

// Mirrors backend ON_SITE_RADIUS_M["regional"] — keep in sync
const REGION_RADIUS_M = 50000;

// Invisible anchor for a region's label chip + popup — the circle itself must
// stay non-interactive or it swallows every click on the map (it covers it)
const regionAnchorIcon = new L.DivIcon({
  html: '',
  className: 'partner-map-region-anchor',
  iconSize: [0, 0],
});

const pinIcon = new L.DivIcon({
  html: `
    <div style="
      background-color: #10B981;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      width: 28px;
      height: 28px;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    "></div>
  `,
  className: 'partner-map-pin',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -26],
  tooltipAnchor: [0, -30],
});

const PartnerAdventureMap = ({ adventures, playLabel, title, anywhereLabel, regionalLabel }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const mapRef = useRef(null);

  const { pinned, regions, anywhere } = useMemo(() => {
    const pinnedOut = [];
    const regionsOut = [];
    const anywhereOut = [];
    for (const adventure of adventures || []) {
      const point = normalizeProjectGeolocation(adventure);
      if (adventure.venueMode === 'regional' && point) {
        regionsOut.push({ ...adventure, point });
      } else if (adventure.venueMode === 'anywhere' || adventure.venueMode === 'regional') {
        anywhereOut.push(adventure);
      } else if (point) {
        pinnedOut.push({ ...adventure, point });
      }
    }
    return { pinned: pinnedOut, regions: regionsOut, anywhere: anywhereOut };
  }, [adventures]);

  const bounds = useMemo(() => {
    if (pinned.length === 0 && regions.length === 0) return null;
    const box = L.latLngBounds([]);
    pinned.forEach((a) => box.extend([a.point.lat, a.point.lng]));
    regions.forEach((a) =>
      box.extend(L.latLng(a.point.lat, a.point.lng).toBounds(REGION_RADIUS_M * 2))
    );
    return box;
  }, [pinned, regions]);

  // Fullscreen toggle changes the container size out from under Leaflet:
  // re-measure, re-frame the pins, and only allow wheel zoom when the map
  // owns the whole screen (embedded wheel zoom hijacks page scrolling).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (bounds) map.fitBounds(bounds, FIT_OPTIONS);
      if (fullscreen) {
        map.scrollWheelZoom.enable();
      } else {
        map.scrollWheelZoom.disable();
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [fullscreen, bounds]);

  // Esc exits fullscreen; lock body scroll while the map owns the screen
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (event) => event.key === 'Escape' && setFullscreen(false);
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreen]);

  if (pinned.length === 0 && regions.length === 0) return null;

  return (
    <section className="mt-14">
      <h3 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-4">
        {title}
      </h3>
      {/* The wrapper owns the size — react-leaflet's MapContainer props are
          immutable after mount, so the map itself is always height:100% and
          we resize its parent (+ invalidateSize) to go fullscreen.
          `isolate` contains Leaflet's internal z-indexes (400–1000), which
          would otherwise float above page overlays like the photo lightbox. */}
      <div
        className={
          fullscreen
            ? 'fixed inset-0 z-50 bg-gray-950 isolate'
            : 'relative h-[380px] rounded-3xl overflow-hidden border border-white/10 isolate'
        }
      >
        <MapContainer
          ref={mapRef}
          bounds={bounds}
          boundsOptions={FIT_OPTIONS}
          zoomControl={true}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Regional guides: honest "roughly around here" footprint, not a
              fake go-here pin. The circle is decoration only — interactive
              it would cover the map and steal clicks from pins beneath it —
              so the clickable element is the name chip on an invisible
              anchor marker. */}
          {regions.map((adventure) => (
            <React.Fragment key={adventure.id}>
              <Circle
                center={[adventure.point.lat, adventure.point.lng]}
                radius={REGION_RADIUS_M}
                interactive={false}
                pathOptions={{
                  color: '#10B981',
                  weight: 2.5,
                  opacity: 0.8,
                  dashArray: '8 8',
                  fillColor: '#10B981',
                  fillOpacity: 0.12,
                }}
              />
              <Marker
                position={[adventure.point.lat, adventure.point.lng]}
                icon={regionAnchorIcon}
                keyboard={false}
              >
                {/* Offset below center: region centers often coincide with a
                    route pin (both seeded on the venue), labels would collide */}
                <Tooltip
                  permanent
                  interactive
                  direction="center"
                  offset={[0, 36]}
                  className="partner-map-label partner-map-label--region"
                >
                  {adventure.name}
                </Tooltip>
                <Popup>
                  <div style={{ minWidth: '170px' }}>
                    <p style={{ fontWeight: 600, margin: '0 0 2px' }}>{adventure.name}</p>
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px' }}>
                      {regionalLabel}
                    </p>
                    <Link
                      to={`/play/${adventure.id}`}
                      style={{
                        display: 'inline-block',
                        padding: '6px 16px',
                        borderRadius: '9999px',
                        background: '#10B981',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '13px',
                        textDecoration: 'none',
                      }}
                    >
                      {playLabel}
                    </Link>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
          {pinned.map((adventure) => (
            <Marker
              key={adventure.id}
              position={[adventure.point.lat, adventure.point.lng]}
              icon={pinIcon}
            >
              {/* Always-on name chip — a bare dot means nothing to a visitor */}
              <Tooltip permanent direction="top" className="partner-map-label">
                {adventure.name}
              </Tooltip>
              <Popup>
                <div style={{ minWidth: '170px' }}>
                  <p style={{ fontWeight: 600, margin: '0 0 8px' }}>{adventure.name}</p>
                  <Link
                    to={`/play/${adventure.id}`}
                    style={{
                      display: 'inline-block',
                      padding: '6px 16px',
                      borderRadius: '9999px',
                      background: '#10B981',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '13px',
                      textDecoration: 'none',
                    }}
                  >
                    {playLabel}
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        <button
          type="button"
          onClick={() => setFullscreen((value) => !value)}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={fullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
          className="absolute top-3 right-3 z-[1000] p-2.5 rounded-xl bg-gray-900/90 hover:bg-gray-900 text-white border border-white/20 shadow-lg transition-colors"
        >
          {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
      {/* Guides not tied to one spot — visible chips, not a footnote, so
          "3 adventures but 1 pin" reads as intentional */}
      {anywhere.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {anywhere.map((adventure) => (
            <Link
              key={adventure.id}
              to={`/play/${adventure.id}`}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white/90 text-xs transition-colors"
            >
              <Compass size={13} className="text-emerald-400/80 flex-shrink-0" />
              <span className="font-medium">{adventure.name}</span>
              <span className="text-white/35">
                · {adventure.venueMode === 'regional' ? regionalLabel : anywhereLabel}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default PartnerAdventureMap;
